/* import __COLOCATED_TEMPLATE__ from './prism-macaroni-chart.hbs'; */
import { set, computed } from '@ember/object';
import Component from '@ember/component';
import { deprecate, assert } from '@ember/debug';
import cssNumericalProperty from '@square/ember-prism/utils/css-numerical-property';
import { scaleLinear } from 'd3-scale';
const MINIMUM_PROPORTION = 0.02;

/**
 * @class PrismMacaroniComponent
 */
export default Component.extend({
  classNames: ['prism-macaroni-chart'],
  init(...args) {
    deprecate('Prop maskSegments on prism-macaroni-chart is deprecated. Set an array of segments instead.', this.maskSegments === null, {
      id: 'ember-prism.macaroni-mask-segments-removal',
      until: '2.0.0',
      for: '@square/ember-prism',
      since: '1.28.2'
    });
    this._super(...args);
  },
  /**
   * @argument classes
   * @type {Array<Array<String>>}
   * @deprecated
   */
  classes: null,
  /**
   * Set `segments` to be an Array of Objects that provide `value` and
   * `className` for the paths rendered by this component.
   *
   * To group together segments in the same visual segment pass an Array of
   * an Array of Objects.
   *
   * For backwards compatibility, you may compute `segments` by setting
   * `classes` instead, but this is deprecated.
   *
   * @argument segments
   * @type {Array<Object>} objects with value and className properties
   * @required
   */
  segments: computed('_segments', 'classes.length', {
    get() {
      if (this._segments) {
        return this._segments;
      }
      if (this.classes) {
        deprecate('Setting classes on prism-macaroni-chart is deprecated. Set segments instead.', this.classes === null, {
          id: 'ember-prism.macaroni-classes-removal',
          until: '2.0.0',
          for: '@square/ember-prism',
          since: '1.28.2'
        });
        assert('Prism Macaroni Chart requires 6 classes!', this.classes.length === 6);
        return this.classes.reduce((segments, segmentClasses, index) => {
          segments.push(...segmentClasses.map((className, partialIndex) => {
            return {
              value: 1 / segmentClasses.length,
              className,
              index,
              partialIndex
            };
          }));
          // eslint-disable-next-line ember/no-side-effects
          set(this, '_segments', segments);
          return this._segments;
        }, []);
      } else {
        // eslint-disable-next-line ember/no-side-effects
        set(this, '_segments', [{
          value: 1
        }]); // default to a single circle
        return this._segments;
      }
    },
    set(key, value) {
      set(this, '_segments', value);
      return this._segments;
    }
  }),
  _normalizedSegments: computed('segments', function () {
    const scalingFunction = scaleLinear([0, 1], [MINIMUM_PROPORTION, 1]);
    let segments = this.segments;
    assert('prism-macaroni-chart requires at least one segment group', segments && segments.length >= 1);
    if (!Array.isArray(segments[0])) {
      segments = [segments];
    }
    const valueSum = segments.reduce((sum, segmentGroup) => {
      return sum + segmentGroup.reduce((sum2, segment) => sum2 + segment.value, 0);
    }, 0);
    return segments.map(segmentGroup => {
      assert('each prism-macaroni-chart segment group requires at least one segment', segmentGroup.length >= 1);
      return segmentGroup.map(segment => {
        const obj = Object.assign({}, segment);
        obj.value = scalingFunction(obj.value / valueSum);
        return obj;
      });
    });
  }),
  _flattenedSegments: computed('_normalizedSegments', function () {
    return this._normalizedSegments.flat();
  }),
  _maskSegments: computed('_normalizedSegments.length', 'maskSegments', function () {
    if (this.maskSegments) {
      return this.maskSegments;
    }
    if (this._normalizedSegments.length === 1) {
      return [];
    }
    return this._normalizedSegments.map(segmentGroup => {
      return {
        value: segmentGroup.reduce((sum, segment) => sum + segment.value, 0)
      };
    });
  }),
  /**
   * Set `maskSegments` to be an Array of Objects that provide `value`. If
   * this property is provided a chart will visually be segmented based on
   * `maskSegments` while the styled segments will be created by `segments`.
   *
   * @argument segments
   * @type {Array<Object>} objects with value and className properties
   */
  maskSegments: null,
  /**
   * An action handler that is called by the component when the user hovers
   * over the path corresponding to the given segment. Receives `segment` on
   * hover and `null` when the mouse exits that segment. Touches also work.
   *
   * @argument onHover
   * @type {Action}
   */
  onHover() {},
  /**
   * Width and height of this chart in pixels.
   *
   * @computed diameter
   * @type {Number}
   */
  diameter: cssNumericalProperty('--diameter'),
  /**
   * Receives hover events from geometry compoent and dispatches `onHover`.
   *
   * @type {Action}
   */
  onGeometryHover(index, partialIndex) {
    if (this.classes) {
      this.onHover(index, partialIndex);
    } else {
      if (index === -1) {
        this.onHover(null);
      } else {
        this.onHover(this.segments.flat()[index]);
      }
    }
  }
});